import React, { useState, useRef } from 'react';
import {
  Box, Button, Typography, Card, CardContent, Grid, Chip, useTheme, alpha, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tabs, Tab, IconButton
} from '@mui/material';
import {
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Inventory as InventoryIcon,
  Timeline as TimelineIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import PHMeasurementStep from './PHMeasurementStep';
import QATestStep from './QATestStep';
import SamplePullStep from './SamplePullStep';
import StabilityStep from './StabilityStep';
import { supabase } from '../../../../supabaseClient';

const ProcedureTags = ({ currentStep, formulation, onStepUpdate, canEdit, canDelete }) => {
  const theme = useTheme();
  const [openDialogs, setOpenDialogs] = useState({
    ph_measurement: false,
    qa_test: false,
    sample_pull: false,
    stability: false
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Refs for each QC component
  const phMeasurementRef = useRef();
  const qaTestRef = useRef();
  const samplePullRef = useRef();
  const stabilityRef = useRef();

  const tagConfig = [
    {
      key: 'ph_measurement',
      label: 'pH Measurement',
      icon: <ScienceIcon />,
      color: theme.palette.primary.main,
      component: PHMeasurementStep,
      dataKey: 'ph_details',
      ref: phMeasurementRef
    },
    {
      key: 'qa_test',
      label: 'QA Test Point', 
      icon: <AssignmentIcon />,
      color: theme.palette.success.main,
      component: QATestStep,
      dataKey: 'qa_test_details',
      ref: qaTestRef
    },
    {
      key: 'sample_pull',
      label: 'Sample Collection',
      icon: <InventoryIcon />,
      color: '#ffa726',
      component: SamplePullStep,
      dataKey: 'sample_details',
      ref: samplePullRef
    },
    {
      key: 'stability',
      label: 'Stability Study',
      icon: <TimelineIcon />,
      color: theme.palette.warning.main,
      component: StabilityStep,
      dataKey: 'stability_details',
      ref: stabilityRef
    }
  ];

  const handleOpenDialog = (tagKey) => {
    setOpenDialogs(prev => ({
      ...prev,
      [tagKey]: true
    }));
  };

  const handleCloseDialog = (tagKey) => {
    setOpenDialogs(prev => ({
      ...prev,
      [tagKey]: false
    }));
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete || !currentStep?.id) return;

    try {
      const tagConf = tagConfig.find(tag => tag.key === tagToDelete);
      if (!tagConf) return;

      const { error } = await supabase
        .from('procedure_steps')
        .update({
          [tagConf.dataKey]: {},
          updated_at: new Date()
        })
        .eq('id', currentStep.id);

      if (error) throw error;

      // Trigger parent refresh
      if (onStepUpdate) {
        onStepUpdate();
      }

      // Force re-render to remove deleted data
      setRefreshTrigger(prev => prev + 1);
      
      // Close confirmation dialog
      setDeleteConfirmOpen(false);
      setTagToDelete(null);

      // Reset active tab to 0 if we deleted the current tab
      setActiveTab(0);

    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Error deleting tag. Please try again.');
    }
  };

  const handleSaveAndClose = async (tagKey) => {
    setIsSaving(true);
    
    try {
      // Find the config for this tag
      const tagConf = tagConfig.find(tag => tag.key === tagKey);
      
      if (tagConf?.ref?.current?.saveData) {
        const success = await tagConf.ref.current.saveData();
        
        if (success) {
          // Trigger parent refresh if available
          if (onStepUpdate) {
            onStepUpdate();
          }
          
          // Force re-render to show updated data
          setRefreshTrigger(prev => prev + 1);
          
          // Close the dialog
          setOpenDialogs(prev => ({
            ...prev,
            [tagKey]: false
          }));
        } else {
          alert('Failed to save data. Please try again.');
        }
      } else {
        // Fallback for components without save function
        setOpenDialogs(prev => ({
          ...prev,
          [tagKey]: false
        }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get available tabs (only show tabs for QC data that exists)
  const availableTabs = tagConfig.filter(tag => {
    const data = currentStep?.[tag.dataKey];
    return data && Object.keys(data).length > 0 && Object.values(data).some(value => value && value !== '' && value !== false);
  });

  const TabPanel = ({ children, value, index }) => (
    <Box
      role="tabpanel"
      hidden={value !== index}
      sx={{ display: value === index ? 'block' : 'none', pt: 3 }}
    >
      {value === index && children}
    </Box>
  );

  const renderQCCard = (tag) => {
    const data = currentStep?.[tag.dataKey];
    if (!data || Object.keys(data).length === 0) return null;

    // Get non-empty fields
    const fields = Object.entries(data)
      .filter(([key, value]) => value && value !== '' && value !== false)
      .map(([key, value]) => ({
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value
      }));

    if (fields.length === 0) return null;

    return (
      <Paper sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(tag.color, 0.3)}`,
        background: `linear-gradient(135deg, ${alpha(tag.color, 0.08)}, ${alpha(tag.color, 0.04)})`,
        boxShadow: `0 12px 32px ${alpha(tag.color, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(tag.color, 0.25)}`,
          border: `1px solid ${alpha(tag.color, 0.5)}`
        }
      }}>
        <TableContainer sx={{ overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  colSpan={2}
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    bgcolor: `linear-gradient(135deg, ${alpha(tag.color, 0.12)}, ${alpha(tag.color, 0.08)})`,
                    borderBottom: `2px solid ${tag.color}`,
                    color: tag.color,
                    py: 2,
                    px: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {tag.icon}
                      {tag.label}
                    </Box>
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setTagToDelete(tag.key);
                          setDeleteConfirmOpen(true);
                        }}
                        sx={{ 
                          color: tag.color,
                          '&:hover': { 
                            bgcolor: alpha(tag.color, 0.1),
                            color: theme.palette.error.main
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, fieldIndex) => (
                <TableRow key={fieldIndex} hover sx={{ 
                  '&:hover': { 
                    bgcolor: alpha(tag.color, 0.05),
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease',
                    boxShadow: 'inset 4px 0 0 ' + tag.color
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2.5,
                    px: 3
                  }
                }}>
                  <TableCell sx={{ fontWeight: 500, width: '25%' }}>{field.label}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: '#495057' }}>
                    {field.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
          Quality Control Components
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add specialized QC procedures to this step
        </Typography>
      </Box>

      {/* Buttons */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tagConfig.map((tag) => (
          <Grid item xs={12} sm={6} md={3} key={tag.key}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={tag.icon}
              onClick={canEdit ? () => handleOpenDialog(tag.key) : undefined}
              disabled={!canEdit}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 500,
                color: canEdit ? tag.color : 'text.disabled',
                borderColor: canEdit ? tag.color : 'action.disabled',
                '&:hover': canEdit ? {
                  bgcolor: alpha(tag.color, 0.1),
                  borderColor: tag.color
                } : {},
                '&.Mui-disabled': {
                  color: 'text.disabled',
                  borderColor: 'action.disabled'
                }
              }}
            >
              Add {tag.label}
            </Button>
          </Grid>
        ))}
      </Grid>

      {/* Tabs for QC Components */}
      <Box key={refreshTrigger}>
        {availableTabs.length > 0 && (
          <Box sx={{ 
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                '& .MuiTab-root': {
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  minHeight: 64,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  '&.Mui-selected': {
                    color: theme.palette.primary.main
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              {availableTabs.map((tag, index) => (
                <Tab
                  key={tag.key}
                  icon={tag.icon}
                  label={tag.label}
                  sx={{ 
                    color: tag.color,
                    '&.Mui-selected': {
                      color: tag.color
                    }
                  }}
                />
              ))}
            </Tabs>

            {availableTabs.map((tag, index) => {
              const TagComponent = tag.component;
              
              return (
                <TabPanel key={tag.key} value={activeTab} index={index}>
                  <Box sx={{ p: 3 }}>
                    {renderQCCard(tag)}
                  </Box>
                </TabPanel>
              );
            })}
          </Box>
        )}

        {availableTabs.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            bgcolor: alpha(theme.palette.grey[500], 0.05),
            borderRadius: 3,
            border: `1px dashed ${alpha(theme.palette.grey[400], 0.5)}`
          }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No QC components added yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click the buttons above to add quality control procedures to this step
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 2 }}>
            Are you sure you want to delete this QC component? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeleteTag}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Delete QC Component
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs for each QC type */}
      {tagConfig.map((tag) => {
        const TagComponent = tag.component;
        
        return (
          <Dialog
            key={tag.key}
            open={openDialogs[tag.key]}
            onClose={() => handleCloseDialog(tag.key)}
            maxWidth="xl"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'grey.100'
              }
            }}
          >
            <DialogTitle sx={{ 
              p: 4, 
              fontWeight: 500, 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: tag.color
            }}>
              {tag.icon}
              {tag.label}
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <TagComponent
                ref={tag.ref}
                currentStep={currentStep}
                formulation={formulation}
              />
            </DialogContent>
            <DialogActions sx={{ p: 4, gap: 2 }}>
              <Button onClick={() => handleCloseDialog(tag.key)}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={() => handleSaveAndClose(tag.key)}
                disabled={isSaving}
                sx={{ bgcolor: tag.color, '&:hover': { bgcolor: tag.color } }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })}
    </Box>
  );
};

export default ProcedureTags;